import { memo, useEffect, useRef } from 'react';
import SearchInput from './SearchInput.jsx';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

const SearchNavigatorBar = ({
  value = '',
  onChange,
  onClear,
  onClose,
  currentIndex = 0,
  totalCount = 0,
  onPrev,
  onNext,
  placeholder = 'Search',
  inputAriaLabel = 'Search',
  prevAriaLabel = 'Previous result',
  nextAriaLabel = 'Next result',
  closeAriaLabel = 'Close search',
  autoFocus = true,
  className = '',
}) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const timeoutId = window.setTimeout(() => {
      wrapRef.current?.querySelector('input')?.focus();
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [autoFocus]);

  const safeTotal = Math.max(0, totalCount);
  const safeCurrent = safeTotal === 0 ? 0 : Math.min(currentIndex + 1, safeTotal);
  const handleClose = onClose ?? onClear;
  const hasMatches = safeTotal > 0;

  return (
    <div
      className={[
        'flex min-w-0 items-stretch gap-0 rounded-xl border border-edge bg-surface shadow-sm transition-colors hover:border-accent/50 focus-within:border-accent/50',
        'dark:border-edge-strong dark:shadow-[0px_2px_8px_rgba(0,0,0,0.25)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex min-w-0 flex-1 items-center px-2 py-1">
        <div
          ref={wrapRef}
          className="[&_input]:w-full [&_input]:border-transparent [&_input]:hover:border-transparent [&_input]:shadow-none [&_input]:ring-0 [&_input]:focus:border-transparent [&_input]:focus:ring-0 w-full min-w-0"
        >
          <SearchInput
            value={value}
            onChange={onChange}
            onClear={onClear}
            placeholder={placeholder}
            aria-label={inputAriaLabel}
            className="w-full"
          />
        </div>
      </div>

      <div className="w-px shrink-0 self-stretch bg-edge" aria-hidden />
      <div className="flex shrink-0 items-center px-2 text-sm tabular-nums text-content-muted">
        {safeCurrent}/{safeTotal}
      </div>
      <div className="w-px shrink-0 self-stretch bg-edge" aria-hidden />

      <div className="flex shrink-0 items-center gap-0.5 pr-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasMatches}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-40"
          aria-label={prevAriaLabel}
        >
          <ChevronUp size={18} aria-hidden />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasMatches}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-40"
          aria-label={nextAriaLabel}
        >
          <ChevronDown size={18} aria-hidden />
        </button>
      </div>

      <div className="w-px shrink-0 self-stretch bg-edge" aria-hidden />
      <button
        type="button"
        onClick={handleClose}
        className="relative w-9 shrink-0 self-stretch rounded-r-xl text-content-secondary hover:bg-surface-hover"
        aria-label={closeAriaLabel}
      >
        <X
          size={18}
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      </button>
    </div>
  );
};

export default memo(SearchNavigatorBar);
